import React, { useCallback, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { FieldProps } from "react-jsonschema-form";
import { ValueType } from "react-select";
import AsyncSelect from "react-select/async";
import { useConfig } from "../../../lib/config";
import { getTimeZoneId } from "../api/googleMapsApi";

interface PlaceOption {
  value: string;
  label: string;
}

interface ExtendedGeocoderResult extends google.maps.GeocoderResult {
  timeZone?: string;
}

const messages = defineMessages({
  fillCoordinates: {
    id: "googlePlacesSelector.fillCoordinates",
    defaultMessage: "Fill coordinate fields",
  },
  placeholder: {
    id: "googlePlacesSelector.placeholder",
    defaultMessage: "Busque una dirección o un lugar...",
  },
});

// Debounce function to delay API calls
const debounce = (func: Function, delay: number) => {
  let timer: NodeJS.Timeout;
  return (...args: any[]) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

function geocodeByPlaceId(apiKey, placeId): Promise<ExtendedGeocoderResult[] | null> {
  const geocoder = new window.google.maps.Geocoder();
  const OK = window.google.maps.GeocoderStatus.OK;

  return new Promise(function (resolve, reject) {
    geocoder.geocode({ placeId: placeId }, async function (
      results: google.maps.GeocoderResult[] | null,
      status
    ) {
      if (status !== OK) {
        reject(status);
      }

      if (results && results.length > 0) {
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();
        try {
          const timeZoneId = await getTimeZoneId(apiKey, lat, lng);
          (results[0] as ExtendedGeocoderResult).timeZone = timeZoneId;
        } catch (error) {
          console.error("Error fetching time zone:", error);
        }
      }

      resolve(results);
    });
  });
}

const fetchPlaces: (inputValue: string) => Promise<undefined | PlaceOption[]> = async (
  inputValue: string
) => {
  if (!inputValue) {
    return;
  }

  try {
    const service = new google.maps.places.AutocompleteService();
    const response = await service.getPlacePredictions({ input: inputValue });
    return response.predictions.map((prediction: google.maps.places.AutocompletePrediction) => ({
      value: prediction.place_id,
      label: `${prediction.description}`,
    }));
  } catch (error) {
    console.error("Error fetching places:", error);
    return [];
  }
};

export function GooglePlacesSelector({
  value,
  onChange,
  addSubmitButton,
  ...otherProps
}: FieldProps & { addSubmitButton?: boolean }) {
  const intl = useIntl();
  const config = useConfig();
  const placeholder = useMemo(() => intl.formatMessage(messages.placeholder), [intl]);
  const [selectedOption, setSelectedOption] = useState<PlaceOption | undefined>(undefined);
  const [geocoderResult, setGeocoderResult] = useState<ExtendedGeocoderResult | undefined>(
    undefined
  );
  const loadOptionsDebounced = useCallback(
    debounce((inputValue: string, callback: (options: any) => void) => {
      fetchPlaces(inputValue).then(options => callback(options));
    }, 500),
    []
  );

  const handleOnChange = useCallback(
    async (valueType: ValueType<PlaceOption, false>) => {
      const option = valueType as PlaceOption;
      const geocoderResults: ExtendedGeocoderResult[] | null = await geocodeByPlaceId(
        config.googleAPiKey,
        option.value
      );
      setSelectedOption(option);
      if (!geocoderResults) {
        setGeocoderResult(undefined);
        return;
      }
      setGeocoderResult(geocoderResults[0]);
    },
    [config.googleAPiKey]
  );

  const handleApplyCoordinates = useCallback(async () => {
    if (geocoderResult) {
      onChange({
        ...selectedOption,
        latitude: geocoderResult.geometry.location.lat(),
        longitude: geocoderResult.geometry.location.lng(),
        timeZone: geocoderResult.timeZone,
      });
      // small hack to make sure the form the coordinates are passed only once after the user selects a place
      setTimeout(() => {
        onChange(selectedOption);
      }, 500);
    }
  }, [geocoderResult, selectedOption, onChange]);

  return (
    <div className="GooglePlacesSelector">
      <label className="control-label">
        {otherProps.schema.title}
        {otherProps.required ? <span className="required">*</span> : ""}
      </label>
      <div className="flex gap-4">
        <AsyncSelect
          defaultOptions
          loadOptions={loadOptionsDebounced}
          value={value}
          onChange={handleOnChange}
          placeholder={placeholder}
          className={"w-full grow-1"}
        />
        {addSubmitButton ? (
          <Button
            className="w-80"
            onClick={handleApplyCoordinates}
            disabled={geocoderResult === undefined}>
            <FormattedMessage {...messages.fillCoordinates} />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
